<script setup lang="ts">
import { computed, inject } from 'vue'
import { ZoPPMDynoPrvokHodnota, ZoPPMDynoSekcia } from '/@src/client-portal'
import {
  DynoSekciaPublicResponse,
  VyzvaSpecificationPublicResponse,
} from '/@src/client-public'
import { formatDateString, formatDateTimeString } from '/@src/utils/format'

export interface DynoSekcia {
  /** Data zadane pouzivatelom v JSON-e. */
  hodnotyDynoPrvkov: ZoPPMDynoSekcia['hodnotyDynoPrvkov']
  /** Data zo spec-u. */
  dynoSekcia: DynoSekciaPublicResponse
}

type OptionType = { value: string; label: string }

const props = withDefaults(defineProps<DynoSekcia>(), {
  hodnotyDynoPrvkov: undefined,
  dynoSekcia: undefined,
})

const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')

function getDynoSekciaValues(name: string, dynoPrvokHodnota: ZoPPMDynoPrvokHodnota) {
  if (!specResponse?.zoppmSpec?.sekcie) {
    return
  }

  const sectionContent = props?.dynoSekcia?.dynoSchema?.sectionContent
  const content =
    typeof sectionContent === 'string' ? JSON.parse(sectionContent) : sectionContent

  const specDynoSekcia =
    content && Array.isArray(content)
      ? content.find((dyno: any) => dyno.name === decodeURIComponent(name))
      : {}

  /** Vyberie prvu definovanu hodnotu (mala by byt len 1) z `hodnotaText`, `hodnotaCislo`, ... */
  const value = Object.values(dynoPrvokHodnota).find((item) => item)

  switch (specDynoSekcia?.type) {
    case 'text':
    case 'textarea':
    case 'tel':
    case 'url':
    case 'email':
    case 'number':
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value: value,
      }
    case 'date':
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value: formatDateString(value),
      }
    case 'time':
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value,
      }
    case 'dateTime':
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value: formatDateTimeString(value),
      }
    case 'combobox':
    case 'radio': {
      const selectedOption: OptionType | undefined =
        specDynoSekcia?.valueSpec?.options?.find(
          (option: OptionType) => option?.value === value
        )
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value: selectedOption?.value,
      }
    }
    case 'checkbox':
    case 'multiselect': {
      const v = (typeof value === 'string' ? JSON.parse(value) : []).flatMap(
        (item: string | any) =>
          specDynoSekcia?.valueSpec?.options
            ?.map((option: OptionType) => {
              if (option?.value === item || option?.value === item?.value) {
                return option?.value
              }

              return
            })
            ?.filter(Boolean)
      )
      return {
        type: specDynoSekcia?.type,
        label: specDynoSekcia?.label,
        value: v,
      }
    }
  }
}

// Vysortujeme dynoprvky podla toho aké poradie je v zoppmSpec
const poradieHodnotDynoPrvkov = computed(() => {
  const specPrvky = specResponse?.zoppmSpec?.sekcie?.find(
    (sekcia) => sekcia?.sekciaRef?.nazov === props?.dynoSekcia?.sekciaRef?.nazov
  )?.dynoSchema?.sectionContent

  const poradieDynoNazvovPrvkov =
    typeof specPrvky === 'string'
      ? JSON.parse(specPrvky).map((prvok: any) => prvok?.name)
      : []

  if (!props.hodnotyDynoPrvkov) {
    return undefined
  }
  const hodnotyPrvkov = Object.keys(props.hodnotyDynoPrvkov)

  const sortedObject = hodnotyPrvkov
    .sort((a, b) => {
      return (
        poradieDynoNazvovPrvkov.indexOf(decodeURIComponent(a)) -
        poradieDynoNazvovPrvkov.indexOf(decodeURIComponent(b))
      )
    })
    .reduce(function (result: any, key: any) {
      result[key] = props.hodnotyDynoPrvkov[key]
      return result
    }, {})

  return sortedObject
})

const dynoSekcie = computed(
  () =>
    poradieHodnotDynoPrvkov.value &&
    Object.entries(poradieHodnotDynoPrvkov.value)
      .map(([value, key]) => getDynoSekciaValues(value, key as any))
      ?.filter(Boolean)
)
</script>

<template>
  <DynoPrvok
    v-for="(sekcia, key) in dynoSekcie"
    :key="key"
    :type="sekcia?.type"
    :value="sekcia?.value"
    :label="sekcia?.label"
  />
</template>
